import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { dateShort, money } from "../lib/format";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";

/**
 * Weekly coverage map for the current data year.
 *
 * The whole panel is collapsed by default. Click the chevron to expand;
 * inside the expanded view, missing weeks are shown first, with an
 * additional toggle to also show present weeks.
 */
export default function CoverageGaps({ reportId, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .coverage(reportId)
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => active && setData({ cms: { weeks: [] }, ups: { weeks: [] } }));
    return () => {
      active = false;
    };
  }, [reportId, refreshKey]);

  return (
    <div
      className="mt-5 pt-5 border-t border-[#E5E5E5]"
      data-testid="coverage-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left hover:bg-[#FAFAFA] rounded-sm -mx-1 px-1 py-1 transition"
        data-testid="coverage-collapse-btn"
        aria-expanded={open}
      >
        <div>
          <div className="eyebrow">
            YTD coverage gaps {data?.year ? `· ${data.year}` : ""}
          </div>
          <div className="text-[12px] text-[#525252] mt-1">
            Weeks run Sat → Fri (UPS billing).{" "}
            {data?.latest_data_date && (
              <>
                Latest data:{" "}
                <span className="font-mono-tabular text-[#0A0A0A]">
                  {dateShort(data.latest_data_date)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {data && <CoverageBadge data={data} />}
          {open ? (
            <ChevronUp className="w-4 h-4 text-[#525252]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#525252]" />
          )}
        </div>
      </button>

      {open && (
        <div className="mt-4" data-testid="coverage-body">
          {!data ? (
            <div className="text-xs text-[#525252] flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading coverage…
            </div>
          ) : (
            <>
              <div className="flex items-center justify-end mb-3">
                <button
                  onClick={() => setShowAll((v) => !v)}
                  className="text-[11px] text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-[#FAFAFA]"
                  data-testid="coverage-show-all-btn"
                >
                  {showAll ? (
                    <>
                      Only show missing <ChevronUp className="w-3 h-3" />
                    </>
                  ) : (
                    <>
                      Show all weeks <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <CoverageColumn
                  title="CMS reports"
                  weeks={data.cms?.weeks || []}
                  showAll={showAll}
                  renderHasData={(w) => `${w.count} shipments · ${money(w.amount)}`}
                  testIdPrefix="cms-week"
                />
                <CoverageColumn
                  title="UPS invoices"
                  weeks={data.ups?.weeks || []}
                  showAll={showAll}
                  renderHasData={(w) =>
                    `${w.invoices.length} invoice${w.invoices.length === 1 ? "" : "s"} · ${w.count} lines`
                  }
                  testIdPrefix="ups-week"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CoverageBadge({ data }) {
  const cmsMissing = (data.cms?.weeks || []).filter((w) => !w.has_data).length;
  const upsMissing = (data.ups?.weeks || []).filter((w) => !w.has_data).length;
  if (cmsMissing === 0 && upsMissing === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0] font-medium">
        <Check className="w-3 h-3" /> Fully covered
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] bg-[#FFF0F0] text-[#DC2626] border border-[#FECACA] font-medium"
      data-testid="coverage-gap-badge"
    >
      <AlertCircle className="w-3 h-3" /> {cmsMissing} CMS · {upsMissing} UPS weeks missing
    </span>
  );
}

function CoverageColumn({ title, weeks, showAll, renderHasData, testIdPrefix }) {
  const missing = weeks.filter((w) => !w.has_data);
  const present = weeks.filter((w) => w.has_data);
  const visible = showAll ? weeks : missing;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{title}</div>
        <span className="text-[11px] font-mono-tabular text-[#525252]">
          {present.length}/{weeks.length} weeks
        </span>
      </div>
      {visible.length === 0 ? (
        <div className="text-xs text-[#525252] border border-dashed border-[#E5E5E5] rounded-sm px-3 py-2">
          {showAll ? "No weeks in range." : "Nothing missing here."}
        </div>
      ) : (
        <ul
          className="space-y-1 max-h-56 overflow-y-auto scroll-thin pr-1"
          data-testid={`${testIdPrefix}-list`}
        >
          {visible.map((w) => (
            <li
              key={w.start}
              className={`flex items-center justify-between text-xs rounded-sm px-3 py-1.5 border ${
                w.has_data
                  ? "border-[#E5E5E5] bg-white"
                  : "border-[#FECACA] bg-[#FFF0F0]"
              }`}
              data-testid={`${testIdPrefix}-${w.start}`}
            >
              <span className="font-mono-tabular text-[12px]">
                {dateShort(w.start)} → {dateShort(w.end)}
              </span>
              <span
                className={`font-mono-tabular text-[11px] ${
                  w.has_data ? "text-[#525252]" : "text-[#DC2626] font-medium"
                }`}
              >
                {w.has_data ? renderHasData(w) : "Missing"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
